/**
 * PriorityScoreEditor — the Phase 3 curation surface for the Priority Engine
 * (docs/PRIORITY_ENGINE.md). Lives as the "Priority" tab of TemplatesWorkspace, next to the other
 * curated firm-standard libraries.
 *
 * WHAT THIS IS FOR: scoring the ~50 TYPES (42 Active deliverable types + ~8 thread types) once, so
 * that every current and future milestone inherits a cost-of-delay and effort without anyone
 * scoring milestones individually. That type-level leverage is the whole reason the burden is ~90
 * one-time judgments rather than hundreds of repeating ones (§6).
 *
 * NOTHING RANKS BY THIS YET. Phase 2 computes the score in shadow mode; Phase 4 is the first time
 * the Flight Plan orders by it. Scoring here is safe and useful in advance — partial scoring works,
 * because anything unscored falls back to today's behavior.
 *
 * DESIGN NOTES
 * - **The scale anchors are always on screen**, not in a tooltip. A 1-13 number with no anchor is a
 *   vibe; "8 = money or a hard date" is repeatable in March by someone who wasn't in the room.
 * - **Fibonacci only.** The picker offers 1/2/3/5/8/13 and nothing else — the widening gaps are the
 *   point (they mirror collapsing confidence as things get bigger), and the database CHECK-constrains
 *   the same set so no future surface can widen it.
 * - **"Never reviewed" is shown distinctly from a real review date** (§7): stale judgment is the
 *   failure mode of a system like this, and a score that looks confirmed but never was is worse than
 *   no score. "Still right" restamps without changing values, so confirming no-change is possible —
 *   the Progress Gate had to learn this the hard way.
 */

import { useEffect, useState, useCallback } from 'react'
import {
  getPriorityInputs, setPriorityScore, clearPriorityScore, confirmPriorityScore, getPriorityPreview,
  COST_OF_DELAY_SCALE, EFFORT_SCALE, indexScores,
} from '../services/priority.js'
import { THREAD_TYPE_ORDER } from '../services/threads.js'

const fmtDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null
const STALE_DAYS = 180   // half a year — a score older than this is worth re-reading, not wrong

export default function PriorityScoreEditor() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [kind, setKind] = useState('deliverable_type')   // which library is being scored
  const [search, setSearch] = useState('')
  const [showUnscoredOnly, setShowUnscoredOnly] = useState(false)

  const load = useCallback(async () => {
    setError('')
    try { setData(await getPriorityInputs()) }
    catch (e) { setError(String(e && e.message || e)) }
  }, [])
  useEffect(() => { load() }, [load])

  const act = useCallback(async (key, fn) => {
    setBusy(key); setError('')
    try { await fn(); await load() }
    catch (e) { setError(String(e && e.message || e)) }
    finally { setBusy('') }
  }, [load])

  if (error && !data) return <div style={S.filler}>{error}</div>
  if (!data) return <div style={S.filler}>Loading…</div>

  // The shadow ranking is its own surface — no scale legend, no scoring pips, and it must be
  // unmistakably labelled as not-live. Sits behind the same tab bar so scoring a type and seeing
  // what it does to the queue is two clicks apart.
  if (kind === 'preview') {
    return (
      <div style={S.wrap}>
        {error && <div style={S.error}>{error}</div>}
        <div style={S.toolbar}>
          <div style={S.tabs}>
            {[['deliverable_type', 'Milestone types'], ['thread_type', 'Thread types'], ['preview', 'Preview ranking']].map(([v, label]) => (
              <button key={v} onClick={() => { setKind(v); setSearch('') }}
                style={{ ...S.tab, ...(kind === v ? S.tabOn : null) }}>{label}</button>
            ))}
          </div>
        </div>
        <PreviewRanking />
      </div>
    )
  }

  const idx = indexScores(data.scores)
  const rows = kind === 'deliverable_type'
    ? (data.deliverableTypes || []).map(t => ({ key: t.id, label: t.name }))
    : Object.keys(THREAD_TYPE_ORDER).sort((a, b) => (THREAD_TYPE_ORDER[a] - THREAD_TYPE_ORDER[b]) || a.localeCompare(b))
        .map(t => ({ key: t, label: t }))

  const q = search.trim().toLowerCase()
  const visible = rows
    .filter(r => !q || r.label.toLowerCase().includes(q))
    .filter(r => {
      if (!showUnscoredOnly) return true
      const s = idx[kind][r.key]
      return !s || s.costOfDelay == null || s.effort == null
    })

  const scoredHere = rows.filter(r => { const s = idx[kind][r.key]; return s && s.costOfDelay != null && s.effort != null }).length

  return (
    <div style={S.wrap}>
      {error && <div style={S.error}>{error}</div>}

      <div style={S.intro}>
        <div style={S.introTitle}>Scoring the queue</div>
        <p style={S.introP}>
          Score the <strong>types</strong>, not individual milestones. Every milestone of a scored type
          inherits these numbers, including ones created next year. Two questions per type: how badly
          it hurts when it is late, and how big the job is. Nothing ranks by this yet — it is safe to
          score in any order, and partial scoring works.
        </p>
      </div>

      <div style={S.toolbar}>
        <div style={S.tabs}>
          {[['deliverable_type', 'Milestone types'], ['thread_type', 'Thread types'], ['preview', 'Preview ranking']].map(([v, label]) => (
            <button key={v} onClick={() => { setKind(v); setSearch('') }}
              style={{ ...S.tab, ...(kind === v ? S.tabOn : null) }}>{label}</button>
          ))}
        </div>
        <input style={S.search} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <label style={S.check}>
          <input type="checkbox" checked={showUnscoredOnly} onChange={e => setShowUnscoredOnly(e.target.checked)} />
          Unscored only
        </label>
        <div style={{ flex: 1 }} />
        <span style={S.count}>{scoredHere} of {rows.length} scored</span>
      </div>

      <ScaleLegend />

      <div style={S.list}>
        {visible.length === 0 && <div style={S.empty}>Nothing matches.</div>}
        {visible.map(r => (
          <ScoreRow
            key={r.key} label={r.label} scopeKind={kind} scopeKey={r.key}
            score={idx[kind][r.key]} busy={busy === r.key}
            onSet={(patch) => act(r.key, () => setPriorityScore(kind, r.key, patch))}
            onClear={() => act(r.key, () => clearPriorityScore(kind, r.key))}
            onConfirm={() => act(r.key, () => confirmPriorityScore(kind, r.key))}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * The shadow ranking — today's order against the new one, for the exact population the nightly
 * scheduler ranks. Loaded on demand (several Airtable round trips, a few seconds), never on mount.
 *
 * **Nothing ranks by this.** The banner says so, permanently and without a dismiss, because a
 * preview that stops announcing itself is how a preview quietly becomes production.
 */
function PreviewRanking() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const run = useCallback(async () => {
    setLoading(true); setErr('')
    try { setData(await getPriorityPreview()) }
    catch (e) { setErr(String(e && e.message || e)) }
    finally { setLoading(false) }
  }, [])

  const st = data && data.stats
  // Biggest movers, both directions — where a model is either obviously right or obviously wrong.
  const movers = data
    ? [...data.rows].filter(r => r.move != null && r.move !== 0).sort((a, b) => Math.abs(b.move) - Math.abs(a.move)).slice(0, 12)
    : []

  return (
    <div style={{ overflowY: 'auto', flex: '1 1 auto', paddingBottom: 20 }}>
      <div style={S.shadowBanner}>
        <strong>Preview only.</strong> Nothing ranks by this. In the Air, the nightly
        scheduler, and every date in the app are untouched — this exists so the model can be judged
        before anything depends on it.
      </div>

      {err && <div style={S.error}>{err}</div>}

      {!data && (
        <div style={{ padding: '14px 0' }}>
          <button style={S.runBtn} onClick={run} disabled={loading}>
            {loading ? 'Computing…' : 'Compute preview ranking'}
          </button>
          <div style={{ ...S.introP, marginTop: 8 }}>
            Reads the live queue and re-ranks it with the scored types. Takes a few seconds.
          </div>
        </div>
      )}

      {data && (
        <>
          <div style={S.statRow}>
            <Stat n={st.population} label="in the queue" />
            <Stat n={st.scored} label="fully scored" tone={st.scored ? undefined : 'warn'} />
            <Stat n={st.partial} label="half-scored" tone={st.partial ? 'warn' : undefined} />
            <Stat n={st.unscored} label="unscored — falling back" />
            <Stat n={st.inheritedFromType} label="inherited from type" />
            <Stat n={st.blocked} label="blocked — can't start" tone={st.blocked ? 'warn' : undefined} />
            <Stat n={st.spearmanOldVsNew == null ? '—' : st.spearmanOldVsNew} label="rank correlation" />
            <div style={{ flex: 1 }} />
            <button style={S.linkBtn} onClick={run} disabled={loading}>{loading ? 'Computing…' : 'Recompute'}</button>
          </div>

          {(data.warnings || []).map((w, i) => <div key={i} style={S.warnLine}>{w}</div>)}

          {st.scored === 0 ? (
            <div style={S.emptyBox}>
              <strong>No new ranking yet — nothing is scored.</strong>
              <div style={{ marginTop: 5 }}>
                Every one of the {st.population} milestones in the queue falls back to today's order,
                which is the correct behaviour and not an error. Score a few milestone types and
                come back; the ranking appears as soon as any type has <em>both</em> a cost of delay
                and an effort.
              </div>
            </div>
          ) : (
            <>
              <div style={S.sectionHead}>Biggest movers</div>
              <div style={S.moverNote}>
                Where the new model most disagrees with today's order. This is the list to read
                critically — if a move looks wrong, the score behind it probably is.
              </div>
              {movers.map(r => (
                <div key={r.milestoneId} style={S.row}>
                  <span style={{ ...S.moveChip, color: r.move > 0 ? '#1FB57A' : '#c0392b' }}>
                    {r.move > 0 ? '▲' : '▼'} {Math.abs(r.move)}
                  </span>
                  <div style={S.rowMain}>
                    <div style={S.rowLabel}>{r.projectName} · {r.name}</div>
                    <div style={S.rowMeta}>
                      <span style={S.metaMuted}>
                        delay {r.costOfDelay} ÷ effort {r.effort}
                        {r.dial !== 1 && ` · project dial ×${r.dial}`}
                        {r.scoreSource === 'type' && ' · inherited from type'}
                        {r.openDownstream > 0 && ` · unblocks ${r.openDownstream}`}
                        {r.pushes > 0 && ` · pushed ${r.pushes}×`}
                      </span>
                    </div>
                  </div>
                  <span style={S.rankCell}>{r.oldRankWithinScored} → {r.newRank}</span>
                </div>
              ))}

              <div style={S.sectionHead}>New order</div>
              {[...data.rows].filter(r => r.newRank != null).sort((a, b) => a.newRank - b.newRank).map(r => (
                <div key={r.milestoneId} style={S.row}>
                  <span style={S.rankNum}>{r.newRank}</span>
                  <div style={S.rowMain}>
                    <div style={S.rowLabel}>
                      {r.blocked && <span style={S.blockedChip}>blocked</span>}
                      {r.projectName} · {r.name}
                    </div>
                    <div style={S.rowMeta}>
                      <span style={S.metaMuted}>
                        score {r.score == null ? '—' : r.score.toFixed(2)}
                        {r.scoreSource === 'type' && ' · inherited from type'}
                      </span>
                      {/* A row boosted by remaining-effort must say WHY, or a finished milestone
                          near the top looks like an unexplained anomaly. */}
                      {(r.pctComplete || 0) >= 0.999 && (
                        <span style={S.metaDone}> · checklist complete, never posted</span>
                      )}
                      {/* Naming the blocker turns "you can't do this" into "go do that instead" */}
                      {r.blocked && (
                        <span style={S.metaWarn}> · waiting on {r.blockedBy || 'a predecessor'}</span>
                      )}
                    </div>
                  </div>
                  <span style={S.rankCell}>was {r.oldRankWithinScored}</span>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  )
}

const Stat = ({ n, label, tone }) => (
  <div style={S.stat}>
    <span style={{ ...S.statN, ...(tone === 'warn' ? { color: '#b8540a' } : null) }}>{n}</span>
    <span style={S.statL}>{label}</span>
  </div>
)

/** The anchors, always visible. A bare 1-13 scale with no shared meaning is not repeatable. */
function ScaleLegend() {
  return (
    <div style={S.legend}>
      <div style={S.legendCol}>
        <div style={S.legendHead}>Cost of delay — what it costs when this is late</div>
        {COST_OF_DELAY_SCALE.map(s => (
          <div key={s.value} style={S.legendRow}>
            <span style={S.legendNum}>{s.value}</span>
            <span style={S.legendLabel}>{s.label}</span>
            <span style={S.legendHint}>{s.hint}</span>
          </div>
        ))}
      </div>
      <div style={S.legendCol}>
        <div style={S.legendHead}>Effort — how big the job is</div>
        {EFFORT_SCALE.map(s => (
          <div key={s.value} style={S.legendRow}>
            <span style={S.legendNum}>{s.value}</span>
            <span style={S.legendLabel}>{s.label}</span>
            <span style={S.legendHint}>{s.hint}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ScoreRow({ label, score, busy, onSet, onClear, onConfirm }) {
  const cod = score ? score.costOfDelay : null
  const eff = score ? score.effort : null
  const complete = cod != null && eff != null
  const never = score && !score.reviewedAt
  const age = score && score.reviewedAt ? daysSince(score.reviewedAt) : null
  const stale = age != null && age > STALE_DAYS

  return (
    <div style={{ ...S.row, opacity: busy ? 0.5 : 1 }}>
      <div style={S.rowMain}>
        <div style={S.rowLabel}>{label}</div>
        <div style={S.rowMeta}>
          {!score && <span style={S.metaMuted}>not scored</span>}
          {score && never && <span style={S.metaWarn}>never reviewed</span>}
          {score && !never && stale && <span style={S.metaWarn}>reviewed {fmtDate(score.reviewedAt)} · {age}d ago</span>}
          {score && !never && !stale && <span style={S.metaMuted}>reviewed {fmtDate(score.reviewedAt)}</span>}
          {score && !complete && <span style={S.metaWarn}> · half-scored</span>}
        </div>
      </div>

      <Picker label="Delay" scale={COST_OF_DELAY_SCALE} value={cod}
        onPick={v => onSet({ costOfDelay: v, effort: eff, note: score && score.note })} />
      <Picker label="Effort" scale={EFFORT_SCALE} value={eff}
        onPick={v => onSet({ costOfDelay: cod, effort: v, note: score && score.note })} />

      <div style={S.rowActions}>
        {score && (never || stale) && (
          <button style={S.linkBtn} onClick={onConfirm} title="Values are still right — restamp the review date">
            Still right
          </button>
        )}
        {score && <button style={S.linkBtnMuted} onClick={onClear} title="Remove this score entirely">Clear</button>}
      </div>
    </div>
  )
}

/** Fibonacci only — the widening gaps are the point. Clicking the active value clears that half. */
function Picker({ label, scale, value, onPick }) {
  return (
    <div style={S.picker}>
      <span style={S.pickerLabel}>{label}</span>
      <div style={S.pickerBtns}>
        {scale.map(s => (
          <button key={s.value}
            onClick={() => onPick(value === s.value ? null : s.value)}
            title={s.label + (s.hint ? ' — ' + s.hint : '')}
            style={{ ...S.pip, ...(value === s.value ? S.pipOn : null) }}>
            {s.value}
          </button>
        ))}
      </div>
    </div>
  )
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '14px 24px 0', fontFamily: '"DM Sans", sans-serif' },
  filler: { padding: 24, fontSize: 13, color: 'var(--aj-text-muted)' },
  error: { fontSize: 12.5, color: '#c0392b', marginBottom: 10 },

  intro: { marginBottom: 12 },
  introTitle: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--aj-text-muted)' },
  introP: { fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.55, margin: '5px 0 0', maxWidth: 780 },

  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  tabs: { display: 'flex', background: 'var(--aj-bg-muted)', borderRadius: 7, padding: 2, gap: 2 },
  tab: { padding: '5px 14px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 12.5, fontFamily: '"DM Sans", sans-serif', background: 'transparent', color: 'var(--aj-text-mid)' },
  tabOn: { background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', fontWeight: 500, boxShadow: '0 1px 2px rgba(0,0,0,0.12)' },
  search: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '5px 10px', width: 220 },
  check: { fontSize: 11.5, color: 'var(--aj-text-mid)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' },
  count: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)' },

  legend: { display: 'flex', gap: 28, padding: '10px 14px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, marginBottom: 12 },
  legendCol: { flex: 1, minWidth: 0 },
  legendHead: { fontSize: 11, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 5 },
  legendRow: { display: 'flex', alignItems: 'baseline', gap: 7, fontSize: 11, lineHeight: 1.7 },
  legendNum: { fontFamily: '"DM Mono", monospace', fontWeight: 600, color: 'var(--aj-text)', width: 16, flexShrink: 0 },
  legendLabel: { color: 'var(--aj-text-mid)', flexShrink: 0 },
  legendHint: { color: 'var(--aj-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  list: { overflowY: 'auto', flex: '1 1 auto', paddingBottom: 20 },
  empty: { fontSize: 12.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '16px 0' },
  row: { display: 'flex', alignItems: 'center', gap: 16, padding: '9px 4px', borderBottom: '0.5px solid var(--aj-border)' },
  rowMain: { flex: '1 1 auto', minWidth: 0 },
  rowLabel: { fontSize: 12.5, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontSize: 10.5, marginTop: 2 },
  metaMuted: { color: 'var(--aj-text-muted)' },
  metaWarn: { color: '#b8540a' },
  metaDone: { color: '#1FB57A' },

  picker: { display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 },
  pickerLabel: { fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--aj-text-muted)' },
  pickerBtns: { display: 'flex', gap: 3 },
  pip: { width: 26, height: 24, borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text-mid)', cursor: 'pointer', fontFamily: '"DM Mono", monospace', fontSize: 11 },
  pipOn: { background: '#07272D', color: '#fff', borderColor: '#07272D', fontWeight: 600 },

  // Preview-ranking surface. The banner is amber and undismissable on purpose — a preview that
  // stops announcing itself is how a preview quietly becomes production.
  shadowBanner: { fontSize: 12, color: '#b8540a', background: 'rgba(184,84,10,0.08)', border: '0.5px solid rgba(184,84,10,0.35)', borderRadius: 6, padding: '8px 12px', lineHeight: 1.5, marginBottom: 12 },
  runBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' },
  statRow: { display: 'flex', alignItems: 'flex-end', gap: 22, padding: '4px 0 12px', borderBottom: '0.5px solid var(--aj-border)', marginBottom: 10 },
  stat: { display: 'flex', flexDirection: 'column', gap: 1 },
  statN: { fontFamily: '"DM Mono", monospace', fontSize: 16, fontWeight: 600, color: 'var(--aj-text)' },
  statL: { fontSize: 10, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' },
  warnLine: { fontSize: 11.5, color: '#b8540a', marginBottom: 5 },
  emptyBox: { fontSize: 12.5, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '14px 16px', lineHeight: 1.55 },
  sectionHead: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--aj-text-muted)', margin: '16px 0 4px' },
  moverNote: { fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5, marginBottom: 8, maxWidth: 700 },
  blockedChip: { fontFamily: '"DM Mono", monospace', fontSize: 9.5, color: '#b8540a', border: '0.5px solid rgba(184,84,10,0.45)', borderRadius: 20, padding: '1px 6px', marginRight: 6 },
  moveChip: { fontFamily: '"DM Mono", monospace', fontSize: 11, fontWeight: 600, width: 42, flexShrink: 0 },
  rankNum: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', width: 42, flexShrink: 0 },
  rankCell: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' },

  rowActions: { display: 'flex', gap: 8, flexShrink: 0, width: 130, justifyContent: 'flex-end' },
  linkBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--aj-accent-blue)', fontFamily: '"DM Sans", sans-serif', padding: 0 },
  linkBtnMuted: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans", sans-serif', padding: 0 },
}
